import type { GfxElement, IBound } from './core/types';
import type { Entity, Target } from '../state/domain';

export type TourStop = {
  key: string;
  title: string;
  bound: IBound;
  /** 期望缩放；为空时按包围盒自动计算。 */
  zoom?: number;
};

const CARD_TYPES = ['note', 'photo'];

function boundOfElement(el: GfxElement): IBound {
  return { x: el.x, y: el.y, w: Math.max(1, el.w), h: Math.max(1, el.h) };
}

function unionBounds(bounds: IBound[]): IBound | null {
  if (bounds.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const b of bounds) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) };
}

function pad(bound: IBound, ratio = 0.12, min = 60): IBound {
  const dx = Math.max(min, bound.w * ratio);
  const dy = Math.max(min, bound.h * ratio);
  return { x: bound.x - dx, y: bound.y - dy, w: bound.w + dx * 2, h: bound.h + dy * 2 };
}

function batch<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * 生成巡航路线（纯函数，便于测试）：
 *   全案全貌 → 逐个对象及其关联线索 → 剩余线索分批 → 研判结论分批
 */
export function buildTourRoute(params: {
  elements: GfxElement[];
  targets: Target[];
  entities: Entity[];
}): TourStop[] {
  const { elements, targets, entities } = params;
  const cards = elements.filter(el => CARD_TYPES.includes(el.type));
  if (cards.length === 0) return [];

  const stops: TourStop[] = [];

  // ① 全案全貌
  const all = unionBounds(cards.map(boundOfElement));
  if (all) stops.push({ key: 'overview', title: '全案全貌', bound: pad(all, 0.16, 120) });

  const byRef = (kind: string, id: string) =>
    cards.filter(el => el.refKind === kind && el.refId === id);

  const covered = new Set<string>();

  // ② 每个对象 + 其关联线索
  for (const target of targets) {
    const own = byRef('target', target.id);
    if (own.length === 0) continue;
    own.forEach(el => covered.add(el.id));
    const clueCards = entities
      .filter(entity => entity.targetId === target.id)
      .flatMap(entity => byRef('entity', entity.id));
    clueCards.forEach(el => covered.add(el.id));
    const bound = unionBounds([...own, ...clueCards].map(boundOfElement));
    if (!bound) continue;
    stops.push({ key: `target:${target.id}`, title: `${target.name} 及其关联线索`, bound: pad(bound) });
  }

  // ③ 未被覆盖的线索分批
  const restClues = cards.filter(el => el.type === 'note' && el.refKind === 'entity' && !covered.has(el.id));
  restClues.forEach(el => covered.add(el.id));
  for (const group of batch(restClues, 6)) {
    const bound = unionBounds(group.map(boundOfElement));
    if (bound) stops.push({ key: `clues:${group[0].id}`, title: '线索分组', bound: pad(bound) });
  }

  // ④ 研判结论分批
  const assessments = cards.filter(el => el.refKind === 'hypothesis');
  assessments.forEach(el => covered.add(el.id));
  for (const group of batch(assessments, 4)) {
    const bound = unionBounds(group.map(boundOfElement));
    if (bound) stops.push({ key: `assess:${group[0].id}`, title: '研判结论', bound: pad(bound) });
  }

  // ⑤ 其余卡片（分区框外的散件等）
  const others = cards.filter(el => !covered.has(el.id));
  if (others.length > 0) {
    const bound = unionBounds(others.map(boundOfElement));
    if (bound) stops.push({ key: 'others', title: '其他卡片', bound: pad(bound) });
  }

  return stops;
}

/** 计算停在某段时应使用的缩放与视口左上角。 */
export function viewportForStop(
  stop: TourStop,
  viewport: { width: number; height: number; minZoom: number; maxZoom: number }
): { zoom: number; viewportX: number; viewportY: number } {
  const topInset = 76;
  const bottomInset = 64;
  const sideInset = 48;
  const availW = Math.max(80, viewport.width - sideInset * 2);
  const availH = Math.max(80, viewport.height - topInset - bottomInset);
  const fit = Math.min(availW / stop.bound.w, availH / stop.bound.h);
  const zoom = Math.min(viewport.maxZoom, Math.max(viewport.minZoom, stop.zoom ?? fit));
  const cx = stop.bound.x + stop.bound.w / 2;
  const cy = stop.bound.y + stop.bound.h / 2;
  const viewportX = cx - viewport.width / 2 / zoom;
  const viewportY = cy - viewport.height / 2 / zoom + (topInset - bottomInset) / 2 / zoom;
  return { zoom, viewportX, viewportY };
}